import { useState, useEffect, useRef } from 'react';
import {
  Shield,
  AlertTriangle,
  Upload,
  Mail,
  Camera,
  HardDrive,
  User,
  FileText,
  Activity,
  Eye,
  Lock,
  Zap,
  Database,
  Search,
  CheckCircle,
  XCircle,
  ChevronRight,
  TrendingUp,
  AlertCircle,
  Settings,
  Briefcase,
  UserX,
  Globe,
  Trash2,
  Cloud,
  ExternalLink,
} from 'lucide-react';

import { THREAT_MATRIX, ALL_THREATS, MAX_POSSIBLE_SCORE, RISK_VALUES, type ThreatItem } from '../data/threatData';
import WelcomeModal from './WelcomeModal';

const WELCOME_KEY = 'itm-welcomed-v2';

const InsiderThreatMatrix = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('proactive');
  const [selectedThreat, setSelectedThreat] = useState<ThreatItem | null>(null);
  const [riskScore, setRiskScore] = useState<number>(0);
  const [activeThreats, setActiveThreats] = useState<ThreatItem[]>([]);
  const [maturityLevel, setMaturityLevel] = useState<number>(2);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showWelcome, setShowWelcome] = useState<boolean>(false);
  const prevCategoryRef = useRef<string>('proactive');

  // Central gate: building your personalised risk profile (toggling threats) is gated UP FRONT — first
  // toggle triggers requireRun (member-free / credits / Tools plan / guest pay). Browsing the matrix + threat
  // details stay public (SEO/reference). gate.js shows the unlock overlay when not entitled.
  const [started, setStarted] = useState<boolean>(false);
  const ensureStarted = async (): Promise<boolean> => {
    if (started) return true;
    const gate = (window as unknown as { csmGate?: { requireRun?: () => Promise<boolean> } }).csmGate;
    const ok = gate?.requireRun ? await gate.requireRun() : true; // graceful: no gate → allow
    if (ok) setStarted(true);
    return ok;
  };

  // Show welcome modal on first visit
  useEffect(() => {
    if (!localStorage.getItem(WELCOME_KEY)) {
      setShowWelcome(true);
    }
    // Restore a prior profile + resume after a guest-pay / sign-in bounce.
    try {
      const saved = JSON.parse(localStorage.getItem('itm_state') || 'null');
      if (saved?.activeThreats) setActiveThreats(saved.activeThreats);
      if (typeof saved?.maturityLevel === 'number') setMaturityLevel(saved.maturityLevel);
    } catch { /* ignore */ }
    const gate = (window as unknown as { csmGate?: { requireRun?: () => Promise<boolean> } }).csmGate;
    if (new URLSearchParams(window.location.search).get('gr_session') && gate?.requireRun) {
      gate.requireRun().then((ok) => { if (ok) setStarted(true); });
    }
  }, []);

  // Persist the profile so it survives the pay/login redirect; save a report for logged-in users (debounced).
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    try { localStorage.setItem('itm_state', JSON.stringify({ activeThreats, maturityLevel })); } catch { /* ignore */ }
    if (activeThreats.length === 0) return;
    const gate = (window as unknown as { csmGate?: { isLoggedIn?: () => boolean; saveReport?: (r: unknown) => Promise<boolean> } }).csmGate;
    if (gate?.isLoggedIn?.() && gate.saveReport) {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        gate.saveReport!({ title: 'Insider Threat Matrix', summary: `Insider threat risk — ${riskScore}%`, data: { riskScore, maturityLevel, activeThreats } });
      }, 2500);
    }
  }, [activeThreats, maturityLevel, riskScore]);

  const handleWelcomeClose = () => {
    localStorage.setItem(WELCOME_KEY, 'true');
    setShowWelcome(false);
  };

  // Auto-switch category tab when search term changes
  useEffect(() => {
    if (searchTerm) {
      prevCategoryRef.current = selectedCategory === 'search' ? prevCategoryRef.current : selectedCategory;
      setSelectedCategory('search');
    } else {
      setSelectedCategory(prevCategoryRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Risk score: weighted severity of active threats as % of maximum possible score
  useEffect(() => {
    if (activeThreats.length === 0) {
      setRiskScore(0);
      return;
    }
    const currentScore = activeThreats.reduce(
      (total, threat) => total + (RISK_VALUES[threat.risk] ?? 0),
      0
    );
    setRiskScore(Math.min(Math.round((currentScore / MAX_POSSIBLE_SCORE) * 100), 100));
  }, [activeThreats]);

  // Search results pseudo-category
  const filteredMatrix = searchTerm
    ? {
        search: {
          title: 'Search Results',
          icon: <Search className="w-5 h-5" />,
          color: 'from-gray-500 to-gray-600',
          description: `Results for "${searchTerm}"`,
          subcategories: [
            {
              name: 'Matching Threats',
              items: ALL_THREATS.filter(
                (threat) =>
                  threat.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  threat.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  threat.indicators.some((i) => i.toLowerCase().includes(searchTerm.toLowerCase()))
              ),
            },
          ],
        },
      }
    : THREAT_MATRIX;

  const maturityLevels = [
    {
      level: 1,
      name: 'Initial',
      description: 'Ad-hoc, reactive approach',
      expectedCoverage: 10,
      priority: ['Proactive Measures', 'High-risk threats'],
      color: 'from-red-500 to-red-600',
    },
    {
      level: 2,
      name: 'Developing',
      description: 'Basic processes in place',
      expectedCoverage: 25,
      priority: ['Policy & Governance', 'Basic monitoring'],
      color: 'from-orange-500 to-orange-600',
    },
    {
      level: 3,
      name: 'Defined',
      description: 'Documented and standardised',
      expectedCoverage: 45,
      priority: ['Behavioural detection', 'Technical controls'],
      color: 'from-yellow-500 to-yellow-600',
    },
    {
      level: 4,
      name: 'Managed',
      description: 'Measured and controlled',
      expectedCoverage: 70,
      priority: ['Advanced analytics', 'Compliance integration'],
      color: 'from-blue-500 to-blue-600',
    },
    {
      level: 5,
      name: 'Optimised',
      description: 'Continuous improvement',
      expectedCoverage: 90,
      priority: ['AI/ML detection', 'Zero trust architecture'],
      color: 'from-green-500 to-green-600',
    },
  ];

  const getRiskBadge = (risk: string): string => {
    const badges: Record<string, string> = {
      critical: 'bg-red-500',
      high: 'bg-orange-500',
      medium: 'bg-yellow-500',
      low: 'bg-green-500',
      info: 'bg-blue-500',
    };
    return badges[risk] ?? badges.info;
  };

  const toggleThreat = async (threat: ThreatItem) => {
    if (!(await ensureStarted())) return; // gate overlay shown — ignore until entitled
    setActiveThreats((prev) => {
      const exists = prev.find((t) => t.id === threat.id);
      return exists ? prev.filter((t) => t.id !== threat.id) : [...prev, threat];
    });
  };

  const isActive = (threatId: string): boolean => activeThreats.some((t) => t.id === threatId);

  const getThreatIcon = (threatId: string): JSX.Element => {
    const iconMap: Record<string, JSX.Element> = {
      'dlp-enforcement': <Lock className="w-4 h-4" />,
      'prevent-backups': <Cloud className="w-4 h-4" />,
      'employee-awareness': <User className="w-4 h-4" />,
      'collaboration-hr': <Briefcase className="w-4 h-4" />,
      'browsing-job-sites': <Globe className="w-4 h-4" />,
      'disciplinary-article': <AlertCircle className="w-4 h-4" />,
      'pending-termination': <UserX className="w-4 h-4" />,
      'screenshots': <Camera className="w-4 h-4" />,
      'downloads-email': <Mail className="w-4 h-4" />,
      'downloads-internal': <Database className="w-4 h-4" />,
      'use-removable': <HardDrive className="w-4 h-4" />,
      'external-email': <Mail className="w-4 h-4" />,
      'upload-3rdparty': <Upload className="w-4 h-4" />,
      'bulk-delete': <Trash2 className="w-4 h-4" />,
      'destruction-physical': <Zap className="w-4 h-4" />,
      'malicious-changes': <Settings className="w-4 h-4" />,
      'anomalous-activity': <Activity className="w-4 h-4" />,
      'shadow-ai-processing': <Cloud className="w-4 h-4" />,
      'ai-model-exfiltration': <Database className="w-4 h-4" />,
    };
    return iconMap[threatId] ?? <FileText className="w-4 h-4" />;
  };

  const getCategoryStats = (categoryKey: string) => {
    const category = THREAT_MATRIX[categoryKey];
    if (!category) return { total: 0, active: 0, critical: 0, high: 0 };
    const items = category.subcategories.flatMap((sub) => sub.items);
    return {
      total: items.length,
      active: items.filter((item) => isActive(item.id)).length,
      critical: items.filter((item) => item.risk === 'critical').length,
      high: items.filter((item) => item.risk === 'high').length,
    };
  };

  const getMaturityInsights = () => {
    const currentMaturity = maturityLevels[maturityLevel - 1];
    const currentCoverage = Math.round((activeThreats.length / ALL_THREATS.length) * 100);
    const expectedCoverage = currentMaturity.expectedCoverage;
    const isOnTrack = currentCoverage >= expectedCoverage;
    const gap = Math.max(0, expectedCoverage - currentCoverage);
    return { isOnTrack, gap, currentCoverage, expectedCoverage, recommendations: currentMaturity.priority };
  };

  const activeMatrix = searchTerm ? filteredMatrix : THREAT_MATRIX;
  const currentCategory = activeMatrix[selectedCategory];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white p-6">
      {showWelcome && <WelcomeModal onClose={handleWelcomeClose} />}

      <div className="max-w-7xl mx-auto">
        {/* Promotional Banner */}
        <div className="mb-6">
          <div className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border border-cyan-500/20 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-gradient-to-r from-cyan-500 to-blue-500 rounded-full flex items-center justify-center">
                    <Shield className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-white">Quick Insider Risk Assessment</h3>
                    <p className="text-xs text-gray-400">Get your personalised insider threat risk score in 5 minutes</p>
                  </div>
                </div>
              </div>
              <a
                href="https://insiderrisk.securitycareers.help/"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105"
              >
                <span>Start Assessment</span>
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <Shield className="w-12 h-12 text-cyan-400 mr-3" />
              <div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
                  Insider Threat Matrix
                </h1>
                <p className="text-gray-400 mt-1">
                  Comprehensive framework for detecting and preventing insider threats · 2026 Edition
                </p>
              </div>
            </div>
            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search threats…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-64 px-4 py-2 pl-10 bg-gray-800/50 backdrop-blur-sm rounded-lg border border-gray-700 focus:border-cyan-500 focus:outline-none transition-colors"
              />
              <Search className="absolute left-3 top-2.5 w-5 h-5 text-gray-400" />
            </div>
          </div>
        </div>

        {/* Risk Dashboard */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          {/* Risk Score */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-300">Risk Score</h3>
              <AlertTriangle
                className={`w-5 h-5 ${
                  riskScore > 60 ? 'text-red-500' : riskScore > 30 ? 'text-yellow-500' : 'text-green-500'
                }`}
              />
            </div>
            <div className="relative h-4 bg-gray-700 rounded-full overflow-hidden mb-2">
              <div
                className={`absolute h-full transition-all duration-500 ${
                  riskScore > 60
                    ? 'bg-gradient-to-r from-red-500 to-red-600'
                    : riskScore > 30
                    ? 'bg-gradient-to-r from-yellow-500 to-orange-500'
                    : 'bg-gradient-to-r from-green-500 to-green-600'
                }`}
                style={{ width: `${riskScore}%` }}
              />
            </div>
            <div className="text-2xl font-bold">{riskScore}%</div>
            <p className="text-xs text-gray-500 mt-1">Severity-weighted exposure</p>
          </div>

          {/* Active Threats */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-300">Active Threats</h3>
              <Activity className="w-5 h-5 text-cyan-400" />
            </div>
            <div className="text-3xl font-bold text-cyan-400">{activeThreats.length}</div>
            <div className="mt-2 text-sm text-gray-400">
              {activeThreats.filter((t) => t.risk === 'critical').length} critical,{' '}
              {activeThreats.filter((t) => t.risk === 'high').length} high risk
            </div>
          </div>

          {/* Coverage */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-300">Coverage</h3>
              <Eye className="w-5 h-5 text-purple-400" />
            </div>
            <div className="text-3xl font-bold text-purple-400">
              {Math.round((activeThreats.length / ALL_THREATS.length) * 100)}%
            </div>
            <div className="mt-2 text-sm text-gray-400">
              {activeThreats.length} of {ALL_THREATS.length} threats monitored
            </div>
          </div>

          {/* Maturity Level */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-300">Maturity Level</h3>
              <TrendingUp className="w-5 h-5 text-green-400" />
            </div>
            <div className="flex items-center space-x-1 mb-3">
              {maturityLevels.map((level) => (
                <div
                  key={level.level}
                  className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold transition-all cursor-pointer ${
                    level.level <= maturityLevel
                      ? `bg-gradient-to-r ${level.color} text-white scale-110 shadow-lg`
                      : 'bg-gray-700 text-gray-500 hover:bg-gray-600'
                  }`}
                  onClick={() => setMaturityLevel(level.level)}
                  title={`${level.name}: ${level.description} (Target: ${level.expectedCoverage}% coverage)`}
                >
                  {level.level}
                </div>
              ))}
            </div>
            <div className="text-sm space-y-2">
              <div className="flex items-center justify-between">
                <span
                  className={`font-semibold bg-gradient-to-r ${
                    maturityLevels[maturityLevel - 1].color
                  } bg-clip-text text-transparent`}
                >
                  {maturityLevels[maturityLevel - 1].name}
                </span>
                {(() => {
                  const insights = getMaturityInsights();
                  return (
                    <div
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        insights.isOnTrack
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                      }`}
                    >
                      {insights.currentCoverage}%&nbsp;/&nbsp;{insights.expectedCoverage}%
                    </div>
                  );
                })()}
              </div>
              <p className="text-gray-400 text-xs">{maturityLevels[maturityLevel - 1].description}</p>
              <div className="text-xs text-gray-500">
                <span className="font-medium">Focus areas:</span>{' '}
                {maturityLevels[maturityLevel - 1].priority.join(', ')}
              </div>
              {(() => {
                const insights = getMaturityInsights();
                if (!insights.isOnTrack) {
                  return (
                    <div className="text-xs text-yellow-400 bg-yellow-500/10 p-2 rounded border border-yellow-500/20">
                      Gap: {insights.gap}% coverage needed to reach {maturityLevels[maturityLevel - 1].name} level
                    </div>
                  );
                }
                return null;
              })()}
            </div>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex space-x-2 mb-6 overflow-x-auto pb-2">
          {Object.entries(activeMatrix).map(([key, category]) => {
            const stats = getCategoryStats(key);
            const isSearch = key === 'search';
            return (
              <button
                key={key}
                onClick={() => setSelectedCategory(key)}
                className={`flex flex-col px-6 py-3 rounded-lg font-medium transition-all whitespace-nowrap min-w-[150px] ${
                  selectedCategory === key
                    ? `bg-gradient-to-r ${category.color} text-white shadow-lg`
                    : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700/50'
                }`}
              >
                <div className="flex items-center space-x-2 mb-1">
                  {category.icon}
                  <span>{category.title}</span>
                </div>
                <div className="text-xs opacity-80">
                  {isSearch
                    ? `${category.subcategories[0]?.items.length ?? 0} results`
                    : `${stats.total} items · ${stats.active} active`}
                </div>
              </button>
            );
          })}
        </div>

        {/* Category Content */}
        {currentCategory && (
          <div className="mb-8">
            <div className="bg-gray-800/30 backdrop-blur-sm rounded-lg p-4 mb-4 border border-gray-700">
              <p className="text-gray-400">{currentCategory.description}</p>
            </div>

            {currentCategory.subcategories.map((subcategory, idx) => (
              <div key={idx} className="mb-6">
                <h3 className="text-xl font-semibold mb-4 text-cyan-400">{subcategory.name}</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {subcategory.items.map((threat) => (
                    <div
                      key={threat.id}
                      className={`bg-gray-800/50 backdrop-blur-sm rounded-lg p-4 border transition-all cursor-pointer hover:shadow-lg ${
                        isActive(threat.id)
                          ? 'border-cyan-500 shadow-cyan-500/20'
                          : 'border-gray-700 hover:border-gray-600'
                      }`}
                      onClick={() => setSelectedThreat(threat)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-start space-x-2 flex-1">
                          <div
                            className={`p-2 rounded-lg bg-gradient-to-r ${currentCategory.color} bg-opacity-20 flex-shrink-0`}
                          >
                            {getThreatIcon(threat.id)}
                          </div>
                          <h3 className="font-semibold text-white text-sm">{threat.name}</h3>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleThreat(threat);
                          }}
                          className={`p-1 rounded-full transition-colors flex-shrink-0 ${
                            isActive(threat.id)
                              ? 'bg-cyan-500 text-white'
                              : 'bg-gray-700 text-gray-400 hover:bg-gray-600'
                          }`}
                        >
                          {isActive(threat.id) ? (
                            <CheckCircle className="w-4 h-4" />
                          ) : (
                            <XCircle className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      <p className="text-sm text-gray-400 mb-3 line-clamp-2">{threat.description}</p>
                      <div className="flex items-center justify-between">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskBadge(
                            threat.risk
                          )} text-white`}
                        >
                          {threat.risk.toUpperCase()}
                        </span>
                        <ChevronRight className="w-4 h-4 text-gray-500" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Threat Detail Modal */}
        {selectedThreat && (
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-6 z-50"
            onClick={() => setSelectedThreat(null)}
          >
            <div
              className="bg-gray-800 rounded-xl p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto border border-gray-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-3 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 bg-opacity-20">
                    {getThreatIcon(selectedThreat.id)}
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-white">{selectedThreat.name}</h2>
                    <span
                      className={`inline-block mt-1 px-3 py-1 rounded-full text-xs font-medium ${getRiskBadge(
                        selectedThreat.risk
                      )} text-white`}
                    >
                      {selectedThreat.risk.toUpperCase()} RISK
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedThreat(null)}
                  className="p-2 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold mb-2 text-cyan-400">Description</h3>
                  <p className="text-gray-300">{selectedThreat.description}</p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2 text-cyan-400">Key Indicators</h3>
                  <ul className="space-y-2">
                    {selectedThreat.indicators.map((indicator, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <AlertCircle className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-300 text-sm">{indicator}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2 text-cyan-400">Recommended Mitigations</h3>
                  <ul className="space-y-2">
                    {selectedThreat.mitigations.map((mitigation, idx) => (
                      <li key={idx} className="flex items-start space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-300 text-sm">{mitigation}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => {
                      toggleThreat(selectedThreat);
                      setSelectedThreat(null);
                    }}
                    className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                      isActive(selectedThreat.id)
                        ? 'bg-gray-700 hover:bg-gray-600 text-gray-300'
                        : 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white'
                    }`}
                  >
                    {isActive(selectedThreat.id) ? 'Remove from Monitoring' : 'Add to Monitoring'}
                  </button>
                  <button
                    onClick={() => setSelectedThreat(null)}
                    className="flex-1 py-2 px-4 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 font-medium transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Active Threats Summary */}
        {activeThreats.length > 0 && (
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700">
            <h3 className="text-xl font-semibold mb-4 text-cyan-400">
              Active Threat Monitoring ({activeThreats.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {activeThreats.map((threat) => (
                <div
                  key={threat.id}
                  className="flex items-center justify-between p-3 bg-gray-900/50 rounded-lg border border-gray-700"
                >
                  <div className="flex items-center space-x-3">
                    {getThreatIcon(threat.id)}
                    <div>
                      <span className="font-medium text-white">{threat.name}</span>
                      <p className="text-xs text-gray-400 mt-1">
                        {threat.description.length > 55
                          ? `${threat.description.substring(0, 55)}…`
                          : threat.description}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 flex-shrink-0 ml-3">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskBadge(
                        threat.risk
                      )} text-white`}
                    >
                      {threat.risk.toUpperCase()}
                    </span>
                    <button
                      onClick={() => toggleThreat(threat)}
                      className="p-1 rounded-full bg-gray-700 hover:bg-gray-600 transition-colors"
                    >
                      <XCircle className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Quick Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-4">
          {Object.entries(THREAT_MATRIX).map(([key, category]) => {
            const stats = getCategoryStats(key);
            return (
              <div
                key={key}
                className="bg-gray-800/30 rounded-lg p-4 text-center cursor-pointer hover:bg-gray-800/50 transition-colors"
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory(key);
                }}
              >
                <div
                  className={`inline-flex p-2 rounded-lg bg-gradient-to-r ${category.color} bg-opacity-20 mb-2`}
                >
                  {category.icon}
                </div>
                <h4 className="text-sm font-medium text-gray-300">{category.title}</h4>
                <div className="text-xs text-gray-500 mt-1">
                  {stats.active}/{stats.total} active
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default InsiderThreatMatrix;
